import { LoginCallback, useOktaAuth } from '@okta/okta-react'
import { useRouter } from 'next/router'
import { useEffect, useState } from 'react'

import { log } from '@common/log/log'
import { LocalStorageKey, useLocalStorage } from '~/components/hooks/useLocalStorage'
import { PageSpinner } from '~/components/PageSpinner'
import { setAccessToken } from '~/data/accessToken'

function OktaLogin() {
  const router = useRouter()
  const { oktaAuth, authState } = useOktaAuth()
  const [isVerifying, setIsVerifying] = useState(false)
  const [expectedEmail] = useLocalStorage(LocalStorageKey.OKTA_EXPECTED_EMAIL)

  useEffect(() => {
    async function loginOktaUser() {
      setIsVerifying(true)
      try {
        const oktaUser = await oktaAuth.getUser()
        const oktaEmail = oktaUser.email

        // Validate that the Okta user matches the email they claimed on the start page
        if (!expectedEmail) {
          log.warn('SSO login error: No expected email found')
          await router.push({
            pathname: '/',
            query: { error: 'SSO session expired. Please try again.' },
          })
          return
        }

        if (oktaEmail?.toLowerCase() !== expectedEmail.toLowerCase()) {
          log.warn('SSO login error: Email mismatch', { oktaEmail, expectedEmail })
          // Sign out of Okta to clear the session
          await oktaAuth.signOut({ clearTokensBeforeRedirect: true })
          await router.push({
            pathname: '/',
            query: { error: `Please log in with ${expectedEmail}` },
          })
          return
        }

        const response = await fetch(`${process.env.NEXT_PUBLIC_BASE_API_URL}/sso-verify`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            oktaJWT: oktaAuth.getAccessToken(),
            email: expectedEmail,
          }),
        })

        const resp = await response.json()

        if (resp.error || resp.errors) {
          const errorMessage = resp.error ?? resp.errors?.[0]?.message ?? 'SSO verification failed'
          log.warn('SSO verification failed', { errorMessage, expectedEmail })
          await router.push({
            pathname: '/',
            query: { error: 'Unable to verify SSO login. Please try again.' },
          })
          return
        }

        setAccessToken(resp.accessToken)
        await router.push({ pathname: '/' })
      } catch (error) {
        log.error('SSO login error', error)
        await router.push({
          pathname: '/',
          query: { error: 'An error occurred during SSO login. Please try again.' },
        })
      } finally {
        setIsVerifying(false)
      }
    }

    if (authState?.isAuthenticated) {
      void loginOktaUser()
    }
  }, [authState, oktaAuth, router, expectedEmail])

  if (isVerifying || authState?.isAuthenticated) {
    return <PageSpinner />
  }

  return authState ? null : <LoginCallback />
}

export default OktaLogin
